import React from 'react';
import { PaymentElement } from '@stripe/react-stripe-js';
import {
  StripeAddressElementOptions,
  StripePaymentElementChangeEvent,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import styles from './StripeForm.module.scss';
import { STRIPE_EXPRESS_CHECKOUT_FEATURE_TOGGLE } from '../../../utils/common/constants';
import {
  ShippingAddress,
  ValidateChangeHandler,
} from '../SteppedPaymentForm/types';
import SelectBillingAddress from '../../SteppedSelectBillingAddress';

const StripeForm = ({
  billingAddress,
  handleIsPaymentButtonDisabled,
  onChangeSelectedPaymentMethod,
  isTouched,
  onChangeBilling,
  shippingAddress,
}: {
  billingAddress: ShippingAddress;
  handleIsPaymentButtonDisabled: (isDisabled: boolean) => void;
  onChangeSelectedPaymentMethod: (
    paymentMethodData?: StripePaymentElementChangeEvent,
  ) => void;
  isTouched?: boolean;
  onChangeBilling: ValidateChangeHandler;
  shippingAddress?: ShippingAddress;
}) => {
  const isStripeExpressCheckoutEnabled = window?.purTogglesEnabled?.(
    STRIPE_EXPRESS_CHECKOUT_FEATURE_TOGGLE,
  );

  const {
    email,
    postalCode,
    streetAddress,
    additionalStreetInfo,
    city,
    state,
    country,
    firstName,
    lastName,
  } = billingAddress;

  const addressOptions: StripeAddressElementOptions = {
    mode: 'billing',
    display: {
      name: 'split',
    },
    blockPoBox: true,
  };
  const options: StripePaymentElementOptions = {
    defaultValues: {
      // Prefill some values from the previous step. Stripe will use this for
      // Link or just prefilling the zip code.
      billingDetails: {
        email,
        name: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
        address: {
          line1: streetAddress,
          line2: additionalStreetInfo,
          city,
          state,
          country: country ?? 'US',
          postal_code: postalCode,
        },
      },
    },
    fields: {
      billingDetails: {
        email: 'auto',
        address: {
          line1: 'auto',
          line2: 'auto',
          city: 'auto',
          state: 'auto',
          country: 'never',
          postalCode: 'auto',
        },
      },
    },
    wallets: {
      link: isStripeExpressCheckoutEnabled ? 'auto' : ('never' as const),
    },
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
      radios: true,
      spacedAccordionItems: true,
      visibleAccordionItemsCount: 10,
    },
    paymentMethodOrder: [
      'card',
      process.env.STRIPE_CUSTOM_SHOP_PAY_ID as string,
      'apple_pay',
      'amazon_pay',
      process.env.STRIPE_CUSTOM_AFFIRM_ID as string,
      'affirm',
      'klarna',
      'afterpay_clearpay',
      process.env.STRIPE_CUSTOM_ACIMA_ID as string,
      process.env.STRIPE_CUSTOM_CHARGEAFTER_ID as string,
      process.env.STRIPE_CUSTOM_TRUEMED_ID as string,
    ],
  };

  /**
   * After a failed payment, detect a change to Stripe's elements, and reset the
   * payment button status so a new method can be submitted.
   */
  const handleOnChange = (e: StripePaymentElementChangeEvent) => {
    // eslint-disable-next-line no-console
    console.log('StripeForm: handleOnChange', e);

    const newSelectedPaymentMethodData = e;

    if (newSelectedPaymentMethodData) {
      onChangeSelectedPaymentMethod(newSelectedPaymentMethodData);
    } else {
      onChangeSelectedPaymentMethod(undefined);
    }
    handleIsPaymentButtonDisabled(false);
  };

  return (
    <div className={styles['stripe-form']}>
      <form>
        <PaymentElement onChange={handleOnChange} options={options} />
        <SelectBillingAddress
          isTouched={isTouched}
          onValidateChange={onChangeBilling}
          isActive={!!shippingAddress}
          addressOptions={addressOptions}
        />
      </form>
    </div>
  );
};

export default StripeForm;
